import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Lightbox from "@/components/feature/Lightbox";
import SafeImage from "@/components/base/SafeImage";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { formatDate, getCategoryBySlug, getCategoryName, getMovieById, relatedMovies } from "@/lib/catalog";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const { ids, toggle } = useFavorites();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useDocumentMeta({
    title: movie ? `${movie.title} - 日本情色电影详细信息` : "影片不存在 - 日本情色电影",
    description: movie
      ? `${movie.title}（${movie.titleJa}，${movie.year}）：导演 ${movie.director}，主演 ${movie.cast.join("、")}，片长 ${movie.runtime} 分钟。${movie.synopsis}`
      : "该影片档案不存在或已调整，请返回首页浏览其他日本情色电影条目。",
    keywords: movie
      ? `${movie.title},${movie.titleJa},${movie.director},${movie.region},日本情色电影,影片资料`
      : "日本情色电影,影片资料",
  });

  if (!movie) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="w-full max-w-lg rounded-lg border border-background-300/60 bg-background-100 p-8 text-center">
            <i className="ri-film-line text-3xl text-accent-500" />
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-50">找不到这部影片</h1>
            <p className="mt-2 text-[13px] leading-relaxed text-foreground-400">
              影片编号可能不正确，或该条目已被调整。你可以返回首页继续浏览。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-[13px] text-foreground-50 transition-colors hover:bg-primary-600 font-label"
            >
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const saved = ids.includes(movie.id);
  const related = relatedMovies(movie, 4);
  const primaryCategory = getCategoryBySlug(movie.categories[0]);

  const handleFavorite = () => {
    const nowSaved = toggle(movie.id);
    setToast(nowSaved ? "已加入收藏，保存在本机浏览器" : "已从收藏夹移除");
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("影片链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-300/50 bg-background-100/40">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
            <Breadcrumb
              items={[
                { label: "分类", to: "/category/classic" },
                { label: primaryCategory ? primaryCategory.name : "影片", to: `/category/${movie.categories[0]}` },
                { label: movie.title },
              ]}
            />
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr] lg:gap-12">
            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="w-full overflow-hidden rounded-lg border border-background-300/60 bg-background-200">
                <div className="aspect-[2/3] w-full">
                  <SafeImage
                    src={movie.poster}
                    alt={`${movie.title} 影片封面`}
                    title={`${movie.title} - 日本情色电影详细信息`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-[12px] transition-colors font-label ${
                    saved
                      ? "bg-primary-500 text-foreground-50 hover:bg-primary-600"
                      : "border border-background-300/80 text-foreground-200 hover:border-accent-500 hover:text-accent-400"
                  }`}
                >
                  <i className={saved ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                  {saved ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 px-4 py-2.5 text-[12px] text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
                >
                  <i className="ri-share-forward-line" />
                  分享链接
                </button>
              </div>

              <p className="mt-3 text-[11px] leading-relaxed text-foreground-600 font-label">
                收藏仅保存在当前浏览器，不需要登录，也不会同步到其他设备。
              </p>

              <div className="mt-5 rounded-lg border border-background-300/60 bg-background-100 p-4">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[10px] tracking-widest2 text-foreground-500 font-label">EDITOR SCORE</p>
                    <p className="mt-1 font-display text-4xl leading-none text-accent-400">
                      {movie.rating.toFixed(1)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] tracking-widest2 text-foreground-500 font-label">热度</p>
                    <p className="mt-1 font-display text-xl text-foreground-200">{movie.popularity}</p>
                  </div>
                </div>
                <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-background-300">
                  <div
                    className="h-full rounded-full bg-accent-500"
                    style={{ width: `${Math.min(100, movie.rating * 10)}%` }}
                  />
                </div>
                <p className="mt-2 text-[10px] text-foreground-600 font-label">评分为编辑示例值，非真实观众评分。</p>
              </div>
            </div>

            <div>
              <p className="flex flex-wrap items-center gap-2 text-[11px] tracking-widest2 text-accent-500 font-label">
                {movie.categories.map((slug) => {
                  const category = getCategoryBySlug(slug);
                  if (!category) return null;
                  return (
                    <Link
                      key={slug}
                      to={`/category/${slug}`}
                      className="cursor-pointer border-b border-accent-500/40 pb-0.5 transition-colors hover:text-accent-300"
                    >
                      {category.name}
                    </Link>
                  );
                })}
              </p>

              <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-2 font-display text-lg italic text-foreground-400 md:text-xl">{movie.titleJa}</p>

              <dl className="mt-7 grid grid-cols-1 gap-y-3 border-y border-background-300/50 py-5 sm:grid-cols-2 sm:gap-x-8">
                {[
                  { label: "导演", value: movie.director },
                  { label: "主演", value: movie.cast.join(" / ") },
                  { label: "上映年份", value: `${movie.year} 年` },
                  { label: "片长", value: `${movie.runtime} 分钟` },
                  { label: "地区", value: movie.region },
                  { label: "上映日期", value: formatDate(movie.releaseDate) },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <dt className="w-16 shrink-0 text-[11px] text-foreground-500 font-label">{item.label}</dt>
                    <dd className="min-w-0 text-[13px] leading-relaxed text-foreground-200">{item.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-7">
                <h2 className="font-heading text-lg font-semibold text-foreground-50">剧情简介</h2>
                <p className="mt-3 text-[13px] leading-relaxed text-foreground-300">{movie.synopsis}</p>
                <div className="mt-4 flex flex-col gap-4">
                  {movie.story.split("\n\n").map((paragraph, index) => (
                    <p key={index} className="text-[13px] leading-[1.9] text-foreground-400">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <h2 className="font-heading text-sm font-semibold text-foreground-100">影片风格与标签</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md border border-background-300/70 bg-background-100 px-2.5 py-1 text-[11px] text-foreground-300 font-label"
                    >
                      #{tag}
                    </span>
                  ))}
                  <span className="rounded-md bg-secondary-800/60 px-2.5 py-1 text-[11px] text-secondary-100 font-label">
                    {getCategoryName(movie.categories)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-300/50 bg-background-100/50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-14">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-[11px] tracking-widest2 text-accent-500 font-label">STILLS</p>
                <h2 className="mt-2 font-heading text-xl font-bold text-foreground-50">片段图集与剧照</h2>
              </div>
              <p className="text-[11px] text-foreground-600 font-label">
                共 {movie.stills.length} 张 · 点击可放大查看，支持方向键切换
              </p>
            </div>

            <div className="mt-6 flex gap-3 overflow-x-auto pb-2 no-scrollbar md:grid md:grid-cols-3 md:overflow-visible">
              {movie.stills.map((still, index) => (
                <button
                  key={still}
                  type="button"
                  onClick={() => setLightboxIndex(index)}
                  className="group relative h-[160px] w-[260px] shrink-0 cursor-pointer overflow-hidden rounded-lg border border-background-300/60 bg-background-200 md:h-auto md:w-full"
                  aria-label={`查看 ${movie.title} 剧照 ${index + 1}`}
                >
                  <div className="aspect-[16/9] w-full">
                    <SafeImage
                      src={still}
                      alt={`${movie.title} 剧照 ${index + 1}`}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <span className="absolute inset-0 flex items-center justify-center bg-background-50/0 opacity-0 transition-opacity group-hover:bg-background-50/30 group-hover:opacity-100">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50/80 text-foreground-100">
                      <i className="ri-zoom-in-line" />
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-[11px] tracking-widest2 text-accent-500 font-label">RELATED</p>
              <h2 className="mt-2 font-heading text-xl font-bold text-foreground-50">相关推荐</h2>
            </div>
            <p className="max-w-md text-[11px] leading-relaxed text-foreground-600">
              依据分类与风格标签匹配，仅供延伸浏览。
            </p>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} showSynopsis={false} />
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1400px] px-4 pb-14 md:px-8">
          <div className="rounded-lg border border-background-300/60 bg-background-100 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-[13px] font-semibold text-foreground-100">
              <i className="ri-shield-check-line text-accent-500" />
              版权声明与免责声明
            </h2>
            <p className="mt-3 text-[12px] leading-relaxed text-foreground-500">
              本页所有影片名称、导演、演员、评分与剧情文字均为演示虚构内容，用于展示网站结构与排版效果，不对应任何真实作品或人物。
              本站不存储、不链接、不提供任何影片文件与播放服务，所有图像均为抽象示意插画。
              页面内容仅作影视资料整理与文化讨论，请读者遵守所在地区法律法规，未成年人请勿浏览。
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />

      <Lightbox
        images={movie.stills}
        index={lightboxIndex}
        title={movie.title}
        onClose={() => setLightboxIndex(null)}
        onChange={(next) => setLightboxIndex(next)}
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-md border border-background-400/60 bg-background-100 px-4 py-2.5 text-[12px] text-foreground-100 font-label">
            <i className="ri-checkbox-circle-line text-accent-400" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}